/**
 * @fileoverview Conflict-check draft that follows the job data (MOR-1846)
 *
 * Purpose: the composer pre-fills "Parties to check" from the suggested
 * brief, but the ASIC/PPSR searches often finish after it opens — the
 * principal place of business, officers and secured creditors land later.
 * The draft used to be filled once and then frozen, so a practitioner could
 * send a brief missing data the job already had.
 *
 * - While the practitioner hasn't edited the draft, it tracks the latest
 *   suggested brief automatically.
 * - Once they edit it, their text is never overwritten. When the job data
 *   changes after that, `updateAvailable` asks them to either take the
 *   updated brief or keep their edits — a deliberate choice either way.
 * - `ready` says whether the draft has reconciled with the CURRENT
 *   suggested brief (MOR-1419 QA F02). Adding or removing an entity re-runs
 *   the suggestion; until it comes back and is applied (or kept), the draft
 *   still describes the old group, and sending it would circulate the old
 *   brief under the new entity list. The composer gates Send on this.
 *
 * Used by: `pipeline-job-panel.tsx` (`ConflictChecksSection`).
 */

import { useCallback, useEffect, useState } from "react";

export interface ConflictBriefDraft {
  description: string;
  setDescription: (next: string) => void;
  /** Job data changed since the practitioner edited the draft. */
  updateAvailable: boolean;
  /**
   * The draft reflects the current suggested brief: it has loaded, and it
   * has either been applied to an untouched draft or explicitly kept or
   * taken over an edited one. False while the suggestion is still loading
   * for a changed entity set, or while `updateAvailable` awaits a choice.
   */
  ready: boolean;
  /** Replace the draft with the latest suggested brief. */
  applyLatest: () => void;
  /** Keep the edited draft; stays quiet until the job data changes again. */
  keepEdits: () => void;
  /** Start over — after sending, the next check starts from fresh data. */
  reset: () => void;
}

/**
 * @param suggestedText - the latest suggested brief; undefined while loading.
 * @param active - whether the composer is open (nothing is applied while shut).
 */
export function useConflictBriefDraft(
  suggestedText: string | undefined,
  active: boolean,
): ConflictBriefDraft {
  const [description, setDescription] = useState("");
  // The brief text last written into the draft from job data; null until the
  // first one is applied.
  const [appliedBrief, setAppliedBrief] = useState<string | null>(null);
  // A suggested brief the practitioner chose not to take.
  const [declinedBrief, setDeclinedBrief] = useState<string | null>(null);

  const untouched =
    appliedBrief === null
      ? description.trim() === ""
      : description === appliedBrief;

  useEffect(() => {
    if (!active || suggestedText === undefined) return;
    if (suggestedText === appliedBrief || !untouched) return;
    setDescription(suggestedText);
    setAppliedBrief(suggestedText);
  }, [active, suggestedText, appliedBrief, untouched]);

  const updateAvailable =
    active &&
    suggestedText !== undefined &&
    !untouched &&
    suggestedText !== appliedBrief &&
    suggestedText !== declinedBrief;

  const applyLatest = useCallback(() => {
    if (suggestedText === undefined) return;
    setDescription(suggestedText);
    setAppliedBrief(suggestedText);
    setDeclinedBrief(null);
  }, [suggestedText]);

  const keepEdits = useCallback(() => {
    setDeclinedBrief(suggestedText ?? null);
  }, [suggestedText]);

  const reset = useCallback(() => {
    setDescription("");
    setAppliedBrief(null);
    setDeclinedBrief(null);
  }, []);

  // An untouched draft is ready once the effect above has written the
  // current suggestion into it; an edited one once no choice is pending.
  const ready =
    suggestedText !== undefined &&
    !updateAvailable &&
    (!untouched || appliedBrief === suggestedText);

  return {
    description,
    setDescription,
    updateAvailable,
    ready,
    applyLatest,
    keepEdits,
    reset,
  };
}
